import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injectable,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Setting } from '@app-state';
import {
    enumMapper,
    IUser,
    ODataResponse,
    USER_ICON_MAP,
    UserType,
} from '@models';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    MapperService,
    ODataQueryOptions,
    RoleService,
} from '@services';
import { extractErrorMessage } from '@utils/object.utils';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class RoleUserSource implements IGridSource<ODataQueryOptions, ODataResponse<IUser>> {
    public roleId!: number;

    public get mapper() {
        return MapperService.userMap;
    }

    constructor(
        private _roleService: RoleService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return this._roleService.getUsersIn(this.roleId, qo);
    }
}

@Component({
    selector: 'ui-role-users-grid',
    templateUrl: './role-users.grid.html',
    providers: [
        RoleService,
        {
            provide: GRID_SERVICE,
            useClass: RoleUserSource,
            deps: [RoleService],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleUsersGridComponent
    extends ODataGridComponent<IUser, number>
    implements OnInit, OnDestroy {

    public readonly UserIconMap = USER_ICON_MAP;
    public readonly isWindowsAuthenticationEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);

    @Input()
    public set roleId(value: number) {
        if (value == null) { return; }

        (this._service as RoleUserSource).roleId = value;
        this._roleId = value;

        this._refresh$.next();
    }

    @Output()
    public close = new EventEmitter<void>();

    public isInConflictingState$ = new Subject<boolean>();

    public typeList = enumMapper(UserType, {
        resourceKey: 'UserType',
    });

    public defaultType = this.isWindowsAuthenticationEnabled
        ? undefined
        : this.typeList.find(x => x.value === UserType.User.toString());

    private _roleService = this._injector.get<RoleService>(RoleService);
    private _roleId!: number;

    ngOnInit() {
        try {
            this._roleService
                .getUserIdsForRole(this._roleId)
                .pipe(takeUntil(this._destroyed$))
                .subscribe(ids => {
                    this._grid.selectionManager.select(
                        ...(ids as IUser[]),
                    );
                    this._grid.selectionManager.snapshot();
                });
        } catch (e) {
            this._snack.error(extractErrorMessage(e));
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.close.complete();
    }

    public onSubmit() {
        this.isInConflictingState$.next(false);

        this._roleService
            .setUsers(this._roleId, this._grid.selectionManager.difference)
            .subscribe(
                () => this.close.next(),
                (errorResponse) => {
                    if (errorResponse.status === 409) {
                        this.isInConflictingState$.next(true);

                        const failedItems: IUser[] = errorResponse
                            .error
                            .resourceIds
                            .map((id: number) => ({ id }));

                        // remove the no longer unavailable user ids from the selection
                        this._grid.selectionManager.deselect(...failedItems);

                        this._refresh$.next();
                    }
                });
    }
}
